import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FaqtoringApi } from '@infrastructure/faqtoring-api';
import { useActorBoundary } from '@application/common/hooks/useActorBoundary';
import { ownershipConflict, provisioningPending } from '@application/features/faqtoring/provisioning';
export function FaqtoringProfile({ invoiceId, onCompleted }) {
    const { t } = useTranslation('faqtoring');
    const boundary = useActorBoundary();
    const [state, setState] = useState();
    const [profile, setProfile] = useState();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState(false);
    const alive = useRef(false);
    const sequence = useRef(0);
    const locked = useRef(false);
    const polls = useRef(0);
    const completed = useRef(onCompleted);
    completed.current = onCompleted;
    const valid = (token) => alive.current && token === sequence.current && boundary.check();
    const accept = (next, token) => {
        if (!valid(token))
            return false;
        if (next.invoiceId !== invoiceId || !Number.isSafeInteger(next.revision) || next.revision < 0)
            throw new Error('Invalid profile context');
        setState(next);
        setProfile(next.profile);
        return true;
    };
    const load = async (poll = false, notify = false) => {
        if (locked.current || !boundary.check())
            return;
        locked.current = true;
        const token = ++sequence.current;
        if (!poll) {
            setBusy(true);
            setError('');
            polls.current = 0;
        }
        try {
            const next = await FaqtoringApi.provisioning(invoiceId);
            if (accept(next, token) && (poll || notify) && next.state === 'completed')
                completed.current();
        }
        catch {
            if (valid(token))
                setError('provisioning.error');
        }
        finally {
            if (valid(token)) {
                locked.current = false;
                setBusy(false);
            }
        }
    };
    useEffect(() => {
        alive.current = true;
        void load();
        return () => { alive.current = false; sequence.current++; locked.current = false; };
    }, [invoiceId, boundary.check]);
    useEffect(() => {
        if (!state || !provisioningPending(state.state) || error || polls.current >= 30)
            return;
        const timer = setTimeout(() => { polls.current++; void load(true); }, 5000);
        return () => clearTimeout(timer);
    }, [state, error]);
    const submit = async (save, retry = false) => {
        if (locked.current || !state || !boundary.check())
            return;
        locked.current = true;
        setBusy(true);
        setError('');
        const token = ++sequence.current;
        try {
            const next = await FaqtoringApi.provision(invoiceId, { expectedRevision: state.revision, ...(save ? { profile } : {}), ...(retry ? { retry: true } : {}) });
            if (accept(next, token)) {
                polls.current = 0;
                setEditing(false);
                if (next.state === 'completed')
                    completed.current();
            }
        }
        catch (e) {
            if (valid(token)) {
                const code = e.response?.data?.code;
                setError(code === 'PROFILE_IBAN_INVALID' ? 'provisioning.ibanInvalid' : ownershipConflict(code) ? 'provisioning.conflict' : code === 'CONCURRENT_CHANGE_RELOAD' ? 'provisioning.changed' : 'provisioning.error');
            }
        }
        finally {
            if (valid(token)) {
                locked.current = false;
                setBusy(false);
            }
        }
    };
    if (boundary.changed)
        return _jsx(Alert, { severity: 'warning', children: t('accountChanged') });
    const pending = state && provisioningPending(state.state);
    const conflict = ownershipConflict(state?.errorCode);
    const canEdit = state?.state === 'completed' || state?.state === 'not_started' || (state?.state === 'blocked' && ['PORTAL_APPROVAL_STALE', 'VALIDATION_ERROR'].includes(state.errorCode || ''));
    const form = canEdit && (editing || (state?.state === 'completed' && !!state.missingFields.length));
    return _jsxs(Box, { sx: { my: 2 }, "aria-busy": busy || !!pending, children: [_jsx(Typography, { variant: 'subtitle1', children: t('provisioning.title') }), error && _jsx(Alert, { severity: 'error', children: t(error) }), !state && !error && _jsx(CircularProgress, { size: 20 }), pending && _jsx(Alert, { severity: 'info', children: t('provisioning.pending') }), state?.state === 'completed' && _jsx(Alert, { severity: state.ready ? 'success' : 'info', children: t(state.ready ? 'provisioning.ready' : 'provisioning.more') }), conflict && _jsx(Alert, { severity: 'warning', children: t('provisioning.conflict') }), state?.state === 'blocked' && !conflict && _jsx(Alert, { severity: 'warning', children: t(state.errorCode === 'PORTAL_APPROVAL_STALE' ? 'provisioning.stale' : state.errorCode === 'PORTAL_ACCESS_REVOKED' ? 'provisioning.revoked' : 'provisioning.blocked') }), state?.state === 'paused' && _jsx(Alert, { severity: 'warning', children: t('provisioning.paused') }), state?.blockers.includes('EMAIL_VERIFICATION_REQUIRED') && _jsx(Alert, { severity: 'info', children: t('provisioning.email') }), state?.blockers.includes('BANK_CHANGE_PENDING') && _jsx(Alert, { severity: 'info', children: t('provisioning.bankPending') }), form && profile && _jsxs(Box, { component: 'form', onSubmit: e => { e.preventDefault(); void submit(true); }, sx: { display: 'grid', gap: 2, my: 2, maxWidth: 560 }, children: [['companyAddress', 'companyPostalCode', 'iban', 'accountHolder'].map(field => _jsx(TextField, { label: t(`provisioning.${field}`), value: profile[field], disabled: busy || !!error, inputProps: { maxLength: field === 'companyAddress' ? 500 : field === 'companyPostalCode' ? 32 : field === 'iban' ? 64 : 200 }, onChange: e => setProfile({ ...profile, [field]: e.target.value }) }, field)), _jsx(Typography, { variant: 'body2', children: t('provisioning.bankNote') }), _jsx(Button, { type: 'submit', variant: 'contained', disabled: busy || !!error, children: t('provisioning.save') })] }), state?.state === 'not_started' && !form && _jsx(Button, { variant: 'contained', disabled: busy || !!error, onClick: () => void submit(false), children: t('provisioning.start') }), canEdit && !form && state?.state !== 'not_started' && _jsx(Button, { disabled: busy || !!error, onClick: () => setEditing(true), children: t('provisioning.edit') }), state?.state === 'blocked' && state.errorCode === 'PORTAL_APPROVAL_STALE' && _jsx(Button, { disabled: busy || !!error, onClick: () => void submit(false), children: t('provisioning.resume') }), (state?.state === 'paused' || conflict || (state?.state === 'blocked' && state.errorCode === 'PORTAL_ACCESS_REVOKED')) && _jsx(Button, { disabled: busy || !!error, onClick: () => void submit(false, true), children: t('provisioning.resume') }), (error || pending || state?.state === 'blocked' || state?.state === 'completed') && _jsx(Button, { disabled: busy, onClick: () => void load(false, true), children: t('refresh') })] });
}
